import type Phaser from 'phaser';
import { PixelCanvas } from './pixel/PixelCanvas';
import { WORLD_PIXEL_W, WORLD_PIXEL_H } from '@/domain/world/types';
import type { BiomeId } from '@/domain/world/types';
import { regionBounds } from '@/domain/world/region';
import type { RegionDefinition } from '@/domain/world/region-definition';
import { regionRegistry } from '@/domain/world/region-registry';
import { mulberry32, hashCode } from '@/domain/world/rng';
import { buildRegionRenderer } from './regions/RegionRendererFactory';
import { registerProceduralRegionRenderer } from './regions/ProceduralRegionRenderer';
import { proceduralPropBounds } from './regions/procedural-obstacles';

/**
 * Renderiza o mundo inteiro (4 biomas) em pixel art real,
 * usando um único Phaser Graphics gigante (2560×1920) que vira textura para câmera.
 *
 * Nota: como WORLD_PIXEL_* já são em "pixels do mundo" (não scale lógico),
 * desenhamos 1:1; o efeito chunky vem de PIXEL scaler global.
 */

export interface WorldRendererHandle {
  destroy(): void;
}

// Paletas por bioma
const PAL = {
  meadow: {
    grass1: 0x6cae5e,
    grass2: 0x528a48,
    grass3: 0x84c870,
    grassDark: 0x3a6235,
    flower1: 0xffe066,
    flower2: 0xff8db1,
    flower3: 0xa0d8ff,
    treeGreen: 0x4a7a3a,
    treeDark: 0x2a4a20,
    treeTrunk: 0x6a4423,
    rock: 0x9aa0a8,
    rockDark: 0x676d75,
  },
  forest: {
    grass1: 0x3a6e3a,
    grass2: 0x2a5028,
    grass3: 0x4a7e48,
    grassDark: 0x183218,
    treeGreen: 0x2a5824,
    treeMid: 0x3e7430,
    treeDark: 0x182f12,
    treeTrunk: 0x4a3018,
    mushroomCap: 0xc04848,
    mushroomDot: 0xfff8e0,
    mushroomStem: 0xf0e6c8,
    fern: 0x6cae5e,
  },
  cave: {
    floor1: 0x4a4658,
    floor2: 0x2c2a3a,
    floor3: 0x5e5872,
    floorDark: 0x18162a,
    rock: 0x6a667a,
    rockDark: 0x3a3848,
    stalg: 0x8a8aa5,
    stalgDark: 0x4a4a65,
    crystal: 0x4ad4ff,
    crystalLight: 0xb0eaff,
    crystalDark: 0x1a6a90,
    moss: 0x4a7a4a,
  },
  coast: {
    sand1: 0xf2d8a6,
    sand2: 0xd4b482,
    sand3: 0xfae6c0,
    sandDark: 0x9c804a,
    water1: 0x4a9adc,
    water2: 0x2a78bc,
    water3: 0x6abade,
    waterFoam: 0xeaf7ff,
    shell1: 0xffe0c8,
    shell2: 0xffc090,
    shellDark: 0x8a5a3a,
    grass: 0x8aaa6a,
  },
} as const;

export function buildWorldRenderer(scene: Phaser.Scene): WorldRendererHandle {
  const regions = regionRegistry.all().map((region) => buildRegionRenderer(scene, region));

  return {
    destroy(): void {
      regions.forEach((region) => region.destroy());
    },
  };
}

export function buildProceduralRegionGraphics(
  scene: Phaser.Scene,
  region: RegionDefinition,
): WorldRendererHandle {
  const root = scene.add.container(0, 0).setDepth(-100);
  const graphics = scene.add.graphics();
  const canvas = new PixelCanvas(graphics);
  root.add(graphics);
  paintBiome(canvas, region.biome, region.bounds);
  paintObstaclesPixel(canvas, region.biome);

  return {
    destroy(): void {
      graphics.destroy();
      root.destroy();
    },
  };
}

registerProceduralRegionRenderer(buildProceduralRegionGraphics);

// ============================================================================
function paintBiome(
  px: PixelCanvas,
  biome: BiomeId,
  b = regionBounds(biome),
): void {
  switch (biome) {
    case 'meadow':
      paintMeadow(px, b);
      break;
    case 'forest':
      paintForest(px, b);
      break;
    case 'cave':
      paintCave(px, b);
      break;
    case 'coast':
      paintCoast(px, b);
      break;
  }
}

function paintMeadow(
  px: PixelCanvas,
  b: { x: number; y: number; w: number; h: number },
): void {
  const p = PAL.meadow;
  // base verde com variação por células de 8px (chunkier)
  for (let y = b.y; y < b.y + b.h; y += 8) {
    for (let x = b.x; x < b.x + b.w; x += 8) {
      const seed = ((x * 7919) ^ (y * 1031)) >>> 0;
      const r = (seed % 100) / 100;
      let c: number;
      if (r < 0.3) c = p.grass1;
      else if (r < 0.7) c = p.grass2;
      else c = p.grass3;
      px.rect(x, y, 8, 8, c);
    }
  }
  // Flores espalhadas (deterministic) — menos quantidade, maiores
  const rng = mulberry32(hashCode(`meadow-flowers`));
  for (let i = 0; i < 30; i++) {
    const fx = b.x + Math.floor(rng() * b.w);
    const fy = b.y + Math.floor(rng() * b.h);
    const r = rng();
    const c = r < 0.4 ? p.flower1 : r < 0.75 ? p.flower2 : p.flower3;
    drawFlower(px, fx, fy, c);
  }
  // Tufos de grama alta — menos quantidade
  const rng2 = mulberry32(hashCode(`meadow-tufts`));
  for (let i = 0; i < 40; i++) {
    const tx = b.x + Math.floor(rng2() * b.w);
    const ty = b.y + Math.floor(rng2() * b.h);
    drawGrassTuft(px, tx, ty, p.grassDark);
  }
}

function paintForest(
  px: PixelCanvas,
  b: { x: number; y: number; w: number; h: number },
): void {
  const p = PAL.forest;
  for (let y = b.y; y < b.y + b.h; y += 8) {
    for (let x = b.x; x < b.x + b.w; x += 8) {
      const seed = ((x * 4567) ^ (y * 2273)) >>> 0;
      const r = (seed % 100) / 100;
      let c: number;
      if (r < 0.3) c = p.grass1;
      else if (r < 0.7) c = p.grass2;
      else c = p.grass3;
      px.rect(x, y, 8, 8, c);
    }
  }
  // Cogumelos espalhados — maiores
  const rng = mulberry32(hashCode(`forest-shrooms`));
  for (let i = 0; i < 14; i++) {
    const mx = b.x + 24 + Math.floor(rng() * (b.w - 48));
    const my = b.y + 24 + Math.floor(rng() * (b.h - 48));
    drawMushroom(px, mx, my, p.mushroomCap, p.mushroomDot, p.mushroomStem);
  }
  // Samambaias
  const rng2 = mulberry32(hashCode(`forest-ferns`));
  for (let i = 0; i < 18; i++) {
    const fx = b.x + Math.floor(rng2() * b.w);
    const fy = b.y + Math.floor(rng2() * b.h);
    drawFern(px, fx, fy, p.fern, p.grassDark);
  }
}

function paintCave(
  px: PixelCanvas,
  b: { x: number; y: number; w: number; h: number },
): void {
  const p = PAL.cave;
  for (let y = b.y; y < b.y + b.h; y += 8) {
    for (let x = b.x; x < b.x + b.w; x += 8) {
      const seed = ((x * 6203) ^ (y * 8819)) >>> 0;
      const r = (seed % 100) / 100;
      let c: number;
      if (r < 0.4) c = p.floor1;
      else if (r < 0.75) c = p.floor2;
      else c = p.floor3;
      px.rect(x, y, 8, 8, c);
    }
  }
  // Pedrinhas espalhadas
  const rng = mulberry32(hashCode(`cave-rocks`));
  for (let i = 0; i < 22; i++) {
    const rx = b.x + Math.floor(rng() * b.w);
    const ry = b.y + Math.floor(rng() * b.h);
    drawSmallRock(px, rx, ry, p.rock, p.rockDark);
  }
  // Cristais menores brilhando
  const rng2 = mulberry32(hashCode(`cave-crystals`));
  for (let i = 0; i < 10; i++) {
    const cx = b.x + 32 + Math.floor(rng2() * (b.w - 64));
    const cy = b.y + 32 + Math.floor(rng2() * (b.h - 64));
    drawCrystal(px, cx, cy, p.crystal, p.crystalLight, p.crystalDark);
  }
  // Vinheta escura nas bordas
  drawCaveVignette(px, b, p.floorDark);
}

function paintCoast(
  px: PixelCanvas,
  b: { x: number; y: number; w: number; h: number },
): void {
  const p = PAL.coast;
  // Faixa de areia (top ~70%) + água (bottom 30%)
  const sandH = Math.floor(b.h * 0.65);
  const waterY = b.y + sandH;
  // Areia
  for (let y = b.y; y < waterY; y += 8) {
    for (let x = b.x; x < b.x + b.w; x += 8) {
      const seed = ((x * 1873) ^ (y * 3911)) >>> 0;
      const r = (seed % 100) / 100;
      let c: number;
      if (r < 0.4) c = p.sand1;
      else if (r < 0.75) c = p.sand2;
      else c = p.sand3;
      px.rect(x, y, 8, 8, c);
    }
  }
  // Água
  for (let y = waterY; y < b.y + b.h; y += 8) {
    for (let x = b.x; x < b.x + b.w; x += 8) {
      const t = (y - waterY) / Math.max(1, b.h - sandH);
      const seed = ((x * 2389) ^ (y * 6113)) >>> 0;
      const r = (seed % 100) / 100;
      let c: number;
      if (t < 0.3) {
        c = r < 0.5 ? p.water1 : p.water3;
      } else {
        c = r < 0.5 ? p.water2 : p.water1;
      }
      px.rect(x, y, 8, 8, c);
    }
  }
  // Linha de espuma na transição
  for (let x = b.x; x < b.x + b.w; x += 16) {
    const seed = (x * 9173) >>> 0;
    const offset = (seed % 6) - 3;
    px.hLine(x, waterY + offset, 12, p.waterFoam);
    px.hLine(x + 4, waterY + offset + 1, 8, p.waterFoam, 0.7);
  }
  // Faixa de água no leste
  const waterEastX = b.x + b.w - 96;
  for (let y = b.y; y < waterY; y += 8) {
    for (let x = waterEastX; x < b.x + b.w; x += 8) {
      const seed = ((x * 3137) ^ (y * 4271)) >>> 0;
      const r = (seed % 100) / 100;
      const c = r < 0.5 ? p.water1 : p.water3;
      px.rect(x, y, 8, 8, c);
    }
  }
  // Conchinhas decorativas
  const rng = mulberry32(hashCode(`coast-shells`));
  for (let i = 0; i < 18; i++) {
    const sx = b.x + 8 + Math.floor(rng() * (b.w - 100));
    const sy = b.y + 8 + Math.floor(rng() * (sandH - 16));
    drawShellSmall(px, sx, sy, p.shell1, p.shell2, p.shellDark);
  }
  // Tufos de grama da praia
  const rng2 = mulberry32(hashCode(`coast-grass`));
  for (let i = 0; i < 12; i++) {
    const gx = b.x + Math.floor(rng2() * (b.w - 96));
    const gy = b.y + 16 + Math.floor(rng2() * (sandH - 32));
    drawGrassTuft(px, gx, gy, p.grass);
  }
}

// ============================================================================
function paintObstaclesPixel(px: PixelCanvas, biome: BiomeId): void {
  const obs = proceduralPropBounds(regionRegistry.get(biome)!);
  switch (biome) {
    case 'meadow': {
      const p = PAL.meadow;
      // Todos obs <= 64×64 são árvores; > são pedras
      for (const o of obs) {
        if (o.h >= 64) drawTreeRound(px, o.x + o.w / 2, o.y + o.h, p.treeGreen, p.treeDark, p.treeTrunk);
        else drawRockBig(px, o.x, o.y, o.w, o.h, p.rock, p.rockDark);
      }
      break;
    }
    case 'forest': {
      const p = PAL.forest;
      for (const o of obs) drawTreePine(px, o.x + o.w / 2, o.y + o.h, p.treeGreen, p.treeMid, p.treeDark, p.treeTrunk);
      break;
    }
    case 'cave': {
      const p = PAL.cave;
      for (const o of obs) {
        if (o.w === 32) drawStalagmite(px, o.x, o.y, o.w, o.h, p.stalg, p.stalgDark);
        else drawCrystalBig(px, o.x, o.y, o.w, o.h, p.crystal, p.crystalLight, p.crystalDark);
      }
      break;
    }
    case 'coast': {
      const p = PAL.coast;
      for (const o of obs) {
        // só pinta as conchas (água já foi pintada)
        if (o.w === 48 && o.h === 32) drawShellBig(px, o.x, o.y, p.shell1, p.shell2, p.shellDark);
      }
      break;
    }
  }
}

export function paintBorders(px: PixelCanvas): void {
  const halfW = WORLD_PIXEL_W / 2;
  const halfH = WORLD_PIXEL_H / 2;
  // Sutil — mostra divisão entre biomas
  for (let y = 0; y < WORLD_PIXEL_H; y += 2) {
    px.px(halfW, y, 0x000000, 0.15);
  }
  for (let x = 0; x < WORLD_PIXEL_W; x += 2) {
    px.px(x, halfH, 0x000000, 0.15);
  }
}

// ============================================================================
// SPRITES PIXEL ART
// ============================================================================

function drawFlower(px: PixelCanvas, cx: number, cy: number, color: number): void {
  // 5 petals + miolo (3×3 com cap centro)
  px.px(cx - 1, cy - 1, color);
  px.px(cx, cy - 1, color);
  px.px(cx + 1, cy - 1, color);
  px.px(cx - 1, cy, color);
  px.px(cx + 1, cy, color);
  px.px(cx - 1, cy + 1, color);
  px.px(cx, cy + 1, color);
  px.px(cx + 1, cy + 1, color);
  px.px(cx, cy, 0xffe066);
  // hastinha
  px.px(cx, cy + 2, 0x3a6235);
  px.px(cx, cy + 3, 0x3a6235);
}

function drawGrassTuft(px: PixelCanvas, cx: number, cy: number, color: number): void {
  px.vLine(cx, cy - 4, 5, color);
  px.vLine(cx - 1, cy - 3, 4, color);
  px.vLine(cx + 1, cy - 3, 4, color);
  px.vLine(cx - 2, cy - 2, 3, color);
  px.vLine(cx + 2, cy - 2, 3, color);
}

function drawMushroom(
  px: PixelCanvas,
  cx: number,
  cy: number,
  cap: number,
  dot: number,
  stem: number,
): void {
  // Cap maior
  px.rect(cx - 5, cy, 11, 3, cap);
  px.rect(cx - 4, cy - 1, 9, 1, cap);
  px.rect(cx - 3, cy - 2, 7, 1, cap);
  px.rect(cx - 2, cy - 3, 5, 1, cap);
  px.px(cx - 1, cy - 4, cap);
  px.px(cx, cy - 4, cap);
  px.px(cx + 1, cy - 4, cap);
  // dots brancas
  px.rect(cx - 3, cy, 2, 2, dot);
  px.rect(cx + 1, cy, 2, 2, dot);
  px.px(cx - 1, cy - 2, dot);
  // stem
  px.rect(cx - 1, cy + 3, 4, 5, stem);
  px.px(cx - 2, cy + 4, stem);
  px.px(cx + 2, cy + 4, stem);
}

function drawFern(px: PixelCanvas, cx: number, cy: number, c1: number, c2: number): void {
  // ramo grande com folhinhas laterais
  px.vLine(cx, cy - 4, 8, c1);
  px.px(cx - 1, cy - 3, c1);
  px.px(cx + 1, cy - 3, c1);
  px.px(cx - 2, cy - 2, c1);
  px.px(cx + 2, cy - 2, c1);
  px.px(cx - 3, cy - 1, c1);
  px.px(cx + 3, cy - 1, c1);
  px.px(cx - 2, cy, c1);
  px.px(cx + 2, cy, c1);
  px.px(cx - 1, cy + 1, c1);
  px.px(cx + 1, cy + 1, c1);
  // sombra na ponta
  px.px(cx - 1, cy + 2, c2);
  px.px(cx + 1, cy + 2, c2);
}

function drawSmallRock(px: PixelCanvas, cx: number, cy: number, c1: number, c2: number): void {
  px.rect(cx - 2, cy, 5, 3, c1);
  px.rect(cx - 1, cy - 1, 3, 1, c1);
  px.px(cx, cy - 2, c1);
  // sombra
  px.hLine(cx - 2, cy + 3, 5, c2);
  px.px(cx - 1, cy + 2, c2);
  px.px(cx + 2, cy + 2, c2);
  // brilho
  px.px(cx - 1, cy, 0xffffff, 0.5);
}

function drawCrystal(
  px: PixelCanvas,
  cx: number,
  cy: number,
  c1: number,
  cLight: number,
  cDark: number,
): void {
  // cristal vertical maior (5×7)
  px.px(cx, cy - 5, cLight);
  px.rect(cx - 1, cy - 4, 3, 1, c1);
  px.rect(cx - 2, cy - 3, 5, 1, c1);
  px.rect(cx - 2, cy - 2, 5, 3, c1);
  px.rect(cx - 1, cy + 1, 3, 1, c1);
  px.px(cx, cy + 2, c1);
  // highlight central
  px.vLine(cx, cy - 4, 6, cLight);
  // sombra direita
  px.vLine(cx + 1, cy - 2, 4, cDark);
  // glow
  px.px(cx - 3, cy - 1, cLight, 0.5);
  px.px(cx + 3, cy - 1, cLight, 0.5);
  px.px(cx, cy - 6, cLight, 0.7);
}

function drawTreeRound(
  px: PixelCanvas,
  cx: number,
  baseY: number,
  green: number,
  dark: number,
  trunk: number,
): void {
  // Árvore copa redonda (32×64 visual)
  // Tronco
  px.rect(cx - 4, baseY - 16, 8, 16, trunk);
  px.vLine(cx - 4, baseY - 16, 16, dark);
  // Copa (3 círculos pixelados)
  drawDisc(px, cx, baseY - 28, 14, green);
  drawDisc(px, cx - 8, baseY - 22, 10, green);
  drawDisc(px, cx + 8, baseY - 22, 10, green);
  // Sombra interna
  drawDisc(px, cx + 4, baseY - 20, 6, dark);
  // Highlight
  drawDisc(px, cx - 4, baseY - 32, 3, 0xc8e890);
  // Sombra no chão
  px.rect(cx - 14, baseY, 28, 3, 0x000000, 0.3);
}

function drawTreePine(
  px: PixelCanvas,
  cx: number,
  baseY: number,
  green: number,
  mid: number,
  dark: number,
  trunk: number,
): void {
  // Pinheiro triangular (3 camadas)
  px.rect(cx - 3, baseY - 12, 6, 12, trunk);
  // 3 níveis
  drawTriangle(px, cx, baseY - 16, 16, mid);
  drawTriangle(px, cx, baseY - 28, 14, green);
  drawTriangle(px, cx, baseY - 40, 12, dark);
  drawTriangle(px, cx, baseY - 50, 8, mid);
  // Topo
  px.px(cx, baseY - 58, 0xfff8c8);
  // sombra chão
  px.rect(cx - 14, baseY, 28, 3, 0x000000, 0.4);
}

function drawTriangle(px: PixelCanvas, cx: number, baseY: number, halfW: number, color: number): void {
  // triângulo equilátero pixel (largura halfW*2, altura halfW)
  const h = halfW;
  for (let y = 0; y < h; y++) {
    const t = y / h;
    const w = Math.round(halfW * (1 - t));
    px.hLine(cx - w, baseY + y, w * 2 + 1, color);
  }
}

function drawDisc(px: PixelCanvas, cx: number, cy: number, r: number, color: number): void {
  for (let y = -r; y <= r; y++) {
    const half = Math.round(r * Math.sqrt(Math.max(0, 1 - (y * y) / (r * r))));
    if (half > 0) px.hLine(cx - half, cy + y, half * 2 + 1, color);
  }
}

function drawRockBig(
  px: PixelCanvas,
  x: number,
  y: number,
  w: number,
  h: number,
  c1: number,
  c2: number,
): void {
  // pedrona oval-ish
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const taper = Math.round((Math.min(t, 1 - t) - 0.2) * w * 0.5);
    const wEff = Math.max(2, w - Math.max(0, -taper * 2));
    const xS = x + Math.floor((w - wEff) / 2);
    const c = t < 0.4 ? c1 : c2;
    px.hLine(xS, y + yy, wEff, c);
  }
  // outline
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const taper = Math.round((Math.min(t, 1 - t) - 0.2) * w * 0.5);
    const wEff = Math.max(2, w - Math.max(0, -taper * 2));
    const xS = x + Math.floor((w - wEff) / 2);
    px.px(xS, y + yy, c2);
    px.px(xS + wEff - 1, y + yy, c2);
  }
  // brilho
  px.rect(x + 3, y + 2, 4, 2, 0xffffff, 0.4);
}

function drawStalagmite(
  px: PixelCanvas,
  x: number,
  y: number,
  w: number,
  h: number,
  c1: number,
  c2: number,
): void {
  // Triângulo invertido (pontudo em cima)
  for (let yy = 0; yy < h; yy++) {
    const t = 1 - yy / h;
    const halfW = Math.max(1, Math.floor((w / 2) * t));
    const cx = x + Math.floor(w / 2);
    const c = yy < h * 0.4 ? c1 : c2;
    px.hLine(cx - halfW, y + yy, halfW * 2 + 1, c);
  }
  // brilho
  px.px(x + Math.floor(w / 2) - 1, y + 4, 0xffffff, 0.5);
}

function drawCrystalBig(
  px: PixelCanvas,
  x: number,
  y: number,
  w: number,
  h: number,
  c1: number,
  cLight: number,
  cDark: number,
): void {
  // Forma de losango/punhal
  const cx = x + Math.floor(w / 2);
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    let halfW: number;
    if (t < 0.3) halfW = Math.floor((w / 2) * (t / 0.3));
    else if (t < 0.7) halfW = Math.floor(w / 2);
    else halfW = Math.floor((w / 2) * ((1 - t) / 0.3));
    halfW = Math.max(1, halfW);
    px.hLine(cx - halfW, y + yy, halfW * 2 + 1, c1);
  }
  // highlight central
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const halfW = Math.max(0, Math.floor((w / 2) * Math.min(t / 0.3, (1 - t) / 0.3)));
    if (halfW > 0) px.px(cx - halfW, y + yy, cLight);
  }
  // sombra direita
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const halfW = Math.max(1, Math.floor((w / 2) * Math.min(t / 0.3, (1 - t) / 0.3)));
    px.px(cx + halfW, y + yy, cDark);
  }
  // brilho topo
  px.px(cx, y + 1, 0xffffff);
  px.px(cx, y + 2, 0xffffff, 0.7);
}

function drawShellSmall(
  px: PixelCanvas,
  cx: number,
  cy: number,
  c1: number,
  c2: number,
  cDark: number,
): void {
  // semicírculo com nervuras
  px.rect(cx - 2, cy, 5, 2, c1);
  px.rect(cx - 1, cy - 1, 3, 1, c1);
  px.px(cx, cy - 2, c2);
  // listras
  px.px(cx - 1, cy, cDark);
  px.px(cx + 1, cy, cDark);
}

function drawShellBig(px: PixelCanvas, x: number, y: number, c1: number, c2: number, cDark: number): void {
  const w = 48;
  const h = 32;
  const cx = x + Math.floor(w / 2);
  // forma oval em cima
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const halfW = Math.floor((w / 2) * Math.sqrt(Math.max(0, 1 - (1 - t) * (1 - t))));
    const c = t < 0.3 ? c1 : c2;
    px.hLine(cx - halfW, y + yy, halfW * 2 + 1, c);
  }
  // nervuras
  for (let i = -3; i <= 3; i++) {
    const lx = cx + i * 5;
    px.vLine(lx, y + 4, h - 6, cDark, 0.5);
  }
  // outline
  for (let yy = 0; yy < h; yy++) {
    const t = yy / h;
    const halfW = Math.floor((w / 2) * Math.sqrt(Math.max(0, 1 - (1 - t) * (1 - t))));
    px.px(cx - halfW, y + yy, cDark);
    px.px(cx + halfW, y + yy, cDark);
  }
}

function drawCaveVignette(
  px: PixelCanvas,
  b: { x: number; y: number; w: number; h: number },
  dark: number,
): void {
  // 4 bordas com fade-in escuro
  for (let i = 0; i < 16; i++) {
    const a = 0.4 * (1 - i / 16);
    px.hLine(b.x, b.y + i, b.w, dark, a);
    px.hLine(b.x, b.y + b.h - 1 - i, b.w, dark, a);
    px.vLine(b.x + i, b.y, b.h, dark, a);
    px.vLine(b.x + b.w - 1 - i, b.y, b.h, dark, a);
  }
}
